import React, { useMemo } from 'react';
import { Help } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { pxToRem } from '@withjoy/joykit/theme';
import { Flex, TextV2, Box, TooltipV2 } from '@withjoy/joykit';
import { HotelPhotoCarousel } from '@shared/components/HotelPhotoCarousel';
import { DEFAULT_HOTEL_IMAGE_WIDE } from '@shared/utils/constants';
import { useHotelRedirectUrl } from '@apps/admin/routes/Planning/routes/Accommodations/hooks/useHotelRedirectUrl';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';
import { AccommodationPlaceType } from '@graphql/generated';
import { HotelRatingV2 } from '@shared/components/HotelRatingV2';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { StarRatingWithTooltip } from '../StarRatingWithTooltip';
import { animationTransition } from '@shared/utils/animationTransition';
import { TravelMapBlockType } from '../../Accommodations.types';
import type { AccommodationPlaceData } from '../../hooks/useAlignAccommodationPlaceData';

const ASPECT_RATIO = '3 / 2';

type HotelPreviewProps = Readonly<{
  hotelData: AccommodationPlaceData;
}>;

export const HotelPreview: React.FC<HotelPreviewProps> = ({ hotelData }) => {
  const { selectedHotelPhotos } = useCustomComponentStateContext();
  const { tq } = useTranslation('travelMap');

  const imagesForCarousel = useMemo(() => {
    const images = [];
    const alt = hotelData.name || '';
    if (hotelData.customPhoto?.url) {
      images.push({ src: hotelData.customPhoto.url, alt });
    }
    if (selectedHotelPhotos?.length) {
      selectedHotelPhotos.forEach(hotelPhoto => {
        images.push({ src: hotelPhoto.url, alt });
      });
    } else if (hotelData.photos?.length) {
      hotelData.photos.forEach((photo: string) => {
        images.push({ src: photo, alt });
      });
    } else if (hotelData.photo?.url) {
      images.push({ src: hotelData.photo.url, alt });
    }
    if (!hotelData.customPhoto?.url && !hotelData.photo?.url) {
      images.push({ src: DEFAULT_HOTEL_IMAGE_WIDE, alt });
    }
    return images;
  }, [hotelData.customPhoto?.url, hotelData.photo?.url, hotelData.photos, hotelData.name, selectedHotelPhotos]);

  const type = hotelData.type === AccommodationPlaceType.hotel ? TravelMapBlockType.travelMapAccommodationsHotel : TravelMapBlockType.travelMapAccommodationsCustom;
  const hotelUrl = useHotelRedirectUrl({
    hotel: { ...hotelData, type },
    searchParams: {
      skipAvailabilityCheck: 'true',
      utm_medium: 'web',
      utm_source: 'admin_accommodations',
      utm_campaign: 'hotel_form',
    },
  });

  const hotelForRating = {
    joyPlaceId: hotelData.joyPlaceId,
    zhId: hotelData.zhId,
    type,
  };

  const linkProps = hotelUrl ? ({ as: 'a', href: hotelUrl, target: '_blank', rel: 'noopener noreferrer' } as const) : ({ as: 'div' } as const);

  return (
    <>
      <Box {...linkProps}>
        <Box
          typographyVariant="hed3"
          textDecorationColor="white"
          _hover={{ textDecoration: 'underline', textDecorationColor: 'mono7' }}
          transition={animationTransition('text-decoration', 'color')}
          css={{ 'text-underline-offset': pxToRem(3) }}
        >
          {hotelData.customName || hotelData.name}
        </Box>
        <Flex
          justifyContent="space-between"
          alignItems="flex-end"
          marginTop={3}
        >
          <Box width="60%">
            <Flex
              gap={5}
              alignItems="center"
              flexWrap="wrap"
            >
              <StarRatingWithTooltip starRating={hotelData.starRating} />
              <DistanceFromVenue
                distances={hotelData.distances}
                milesToVenue={hotelData.distance}
              />
            </Flex>
            {hotelUrl && (
              <TextV2
                marginTop={4}
                typographyVariant="label4"
                color="newUIBrandPrimary"
                borderBottom="1px dotted"
                borderBottomColor="newUIBrandPrimary"
                _hover={{ cursor: 'pointer' }}
                fontSize={pxToRem(14)}
                fontWeight={500}
                width="fit-content"
              >
                {tq`Explore This Property →`}
              </TextV2>
            )}
          </Box>
          <HotelRatingV2
            hotel={hotelForRating}
            context="admin-hotel-form"
          />
        </Flex>
      </Box>

      <Flex
        gap={1}
        alignItems="center"
        marginTop={7}
      >
        <TextV2 typographyVariant="button2">{tq`Thumbnail`}</TextV2>
        <TooltipV2
          overrides={{
            Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white' } },
          }}
          content={
            <Flex
              flexDirection="column"
              maxWidth={pxToRem(256)}
            >
              <Box typographyVariant="label3">{tq`Thumbnail`}</Box>
              <Box
                typographyVariant="label2"
                color="mono4"
              >
                {tq`This is a preview of how this hotel will be displayed on your website. Guests click on this to view more details and book from your website.`}
              </Box>
            </Flex>
          }
          onMouseEnterDelay={500}
        >
          <Help
            size={16}
            color="mono10"
          />
        </TooltipV2>
      </Flex>
      <Box
        width="100%"
        height="100%"
        marginTop={5}
      >
        <Box
          aspectRatio={ASPECT_RATIO}
          position={'relative'}
          backgroundColor={'mono2'}
          overflow={'hidden'}
          borderRadius={pxToRem(12)}
        >
          <HotelPhotoCarousel
            hotel={hotelData}
            images={imagesForCarousel}
            context="Accommodations.HotelForm"
            aspectRatio={ASPECT_RATIO}
            navigation={{ variant: 'minimal', visible: 'default' }}
          />
        </Box>
      </Box>
    </>
  );
};
